import { ApolloError } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useMemo, useState } from "react";

import { AddNodeInput, NodeLevel } from "../../../API";
import type { Product } from "../../../API";
import CodeSnippetBox from "../../../common/components/box/CodeSnippetBox";
import StyledDialog from "../../../common/components/dialog/StyledDialog";
import DeviceTemplateNodeSelect from "../../../common/components/layout/components/DeviceTemplateNodeSelect";
import LocationIdSelect from "../../../common/components/layout/components/LocationIdSelect";
import SKUSelect from "../../../common/components/layout/components/SKUSelect";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerExternalIdGuard } from "../../../common/hooks/useCustomerExternalIdGuard";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useGetCustomer } from "../../../common/hooks/useGetCustomer";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { skuCatalog } from "../../../common/models/enums";
import { checkDuplicateDeviceNames } from "../../../common/utils/checkDuplicateDeviceNames";
import {
  generateNodeName,
  resolveNodeNameConflicts,
} from "../../../common/utils/generateNodeName";
import { mapTemplateDataToDeviceInput } from "../../../common/utils/mapTemplateDataToDeviceInput";
import { mapTemplateDataToServiceInput } from "../../../common/utils/mapTemplateDataToServiceInput";
import { validateCreateDevicesFromTemplate } from "../../../common/utils/validateCreateDevicesFromTemplate";
import { DEFAULT_DEVICE_PASSWORD } from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocation } from "../../customer-settings/tabs/locations/hooks/useGetDevicesByLocation";
import { useGetLocationsCached } from "../../customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useCreateNode } from "../../nodes/hooks/useCreateNode";
import { useGetNodes } from "../../nodes/hooks/useGetNodes";
import { useUpdateNode } from "../../nodes/hooks/useUpdateNode";
import { useAddProduct } from "../hooks/useAddProduct";
import { useGetProductsByLocation } from "../hooks/useGetProductsByLocation";
import { useCreateBatchDevices } from "../hooks/useCreateBatchDevices";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";

type OnboardingComponent = {
  name: string;
  type: string;
  makeModelId: string;
  hasPT: boolean;
  models: string[];
};

type NodeRow = {
  location: AutocompleteOptionType;
  nodeName: string;
  nodeIP: string;
  componentSerialNumber: string;
};

type CreatedNodeInfo = {
  nodeId: string;
  nodeName: string;
  nodeIP: string;
  onboardCommand: string;
};

type NodeAssignment = {
  nodeId: string;
  nodeIP: string;
  nodeName: string;
  location?: string;
};

interface SelectedSKU {
  id: string;
  skuNumber: string;
  configurationName: string;
  category: string;
  skuDataConfigurations: string;
  nodeCount: number;
  defaultIp?: string | null;
}

interface CreateDevicesFromSKUDialogProps {
  open: boolean;
  handleClose: () => void;
}

const IPV4_REGEX =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const CreateDevicesFromSKUDialog = ({
  open,
  handleClose,
}: CreateDevicesFromSKUDialogProps) => {
  // const theme = useTheme();
  const confirm = useConfirm();
  const selectedCustomerId = useCustomerIdGuard();
  const customerExternalId = useCustomerExternalIdGuard();
  const selectedLocation = useSelectedLocationDropdown();
  const { data: allNodesData, refetch: refetchAllNodes } = useGetNodes({
    customerId: selectedCustomerId,
    includeDevices: true,
  });
  const { data: currentDeviceData, refetchDevices } = useGetDevicesByLocation(
    selectedLocation?.value ?? ""
  );
  const { customer } = useGetCustomer();
  const { syncShadowsForNode } = useSyncShadow();
  const { batchCreateDevices, loading: devicesLoading } =
    useCreateBatchDevices();
  const { batchCreateServices } = useCreateBatchServices();
  const { createNode } = useCreateNode();
  const { updateNode } = useUpdateNode();
  const { addProduct } = useAddProduct();
  const { products: locationProducts } = useGetProductsByLocation(
    selectedLocation?.value ?? ""
  );
  const { locations } = useGetLocationsCached();

  // Step navigation
  const [activeStep, setActiveStep] = useState(0);

  // Step 0 state
  const [selectedSKU, setSelectedSKU] = useState<SelectedSKU | null>(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [devicePassword, setDevicePassword] = useState("");
  const [step0Submitted, setStep0Submitted] = useState(false);

  // Step 1 state (node setup)
  const [nodeRows, setNodeRows] = useState<NodeRow[]>([]);
  const [createdNodes, setCreatedNodes] = useState<CreatedNodeInfo[]>([]);
  const [nodesCreated, setNodesCreated] = useState(false);
  const [nodeCreationLoading, setNodeCreationLoading] = useState(false);
  const [step1Submitted, setStep1Submitted] = useState(false);

  // Step 2 state (devices)
  const [components, setComponents] = useState<OnboardingComponent[]>([]);
  const [deviceIpAddresses, setDeviceIpAddresses] = useState<string[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<NodeAssignment[]>([]);
  const [componentSerialNumbers, setComponentSerialNumbers] = useState<
    string[]
  >([]);
  const [step2Submitted, setStep2Submitted] = useState(false);

  // Step 1 accessory state
  const [selectedParentProduct, setSelectedParentProduct] =
    useState<Product | null>(null);
  const [selectedAccessoryNode, setSelectedAccessoryNode] =
    useState<NodeAssignment>({
      nodeId: "",
      nodeIP: "",
      nodeName: "",
    });
  const [step1AccessorySubmitted, setStep1AccessorySubmitted] = useState(false);
  const [useStandaloneAccessory, setUseStandaloneAccessory] = useState(false);

  const nodeCount = selectedSKU?.nodeCount ?? 0;
  const isLinkAccessory = (selectedSKU?.nodeCount ?? 0) === 0;
  const steps =
    nodeCount >= 1
      ? ["Configuration", "Node Setup", "Devices & Submit"]
      : ["Configuration", "Node Selection", "Devices & Submit"];
  const deviceStepIndex = nodeCount >= 1 || isLinkAccessory ? 2 : 1;

  useEffect(() => {
    if (!devicePassword) {
      setDevicePassword(customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer?.devicePassword]);

  const allCustomerNodes = useMemo(() => {
    if (!allNodesData) return [];

    return (
      allNodesData.getNodes?.items
        ?.map(node => {
          const rawLocationId =
            node?.locationId?.replace("C_", "C#").replace("_L_", "#L#") ?? "";
          const locationName =
            locations?.find(l => l?.id === rawLocationId)?.name ?? "";

          return {
            nodeId:
              node?.nodeId
                ?.replace("C_", "C#")
                .replace("_L_", "#L#")
                .replace("_N_", "#N#") ?? "",
            nodeIP: node?.nodeIP ?? "",
            nodeName: node?.nodeName ?? "",
            location: locationName,
          };
        })
        .filter(n => n.nodeId && n.nodeIP && n.nodeName)
        .sort((a, b) => a.location.localeCompare(b.location)) ?? []
    );
  }, [allNodesData, locations]);

  const existingNames =
    currentDeviceData?.getDevicesByLocation?.items
      ?.map(d => d?.id?.replace(/^DE#/, "").toLowerCase())
      .filter((id): id is string => !!id) ?? [];

  const resetDialogState = () => {
    setActiveStep(0);

    setSelectedSKU(null);

    setSerialNumber("");

    setDevicePassword("");

    setStep0Submitted(false);

    setNodeRows([]);

    setCreatedNodes([]);

    setNodesCreated(false);

    setNodeCreationLoading(false);

    setStep1Submitted(false);

    setComponents([]);

    setDeviceIpAddresses([]);

    setSelectedNodes([]);

    setComponentSerialNumbers([]);

    setStep2Submitted(false);

    setSelectedParentProduct(null);

    setSelectedAccessoryNode({ nodeId: "", nodeIP: "", nodeName: "" });

    setStep1AccessorySubmitted(false);

    setUseStandaloneAccessory(false);
  };

  const closeDialog = (): void => {
    handleClose();

    resetDialogState();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" — keep open
      });
  };

  const handleSKUChange = ({
    id,
    skuNumber,
    configurationName,
    category,
    skuDataConfigurations,
    nodeCount: newNodeCount,
    defaultIp,
  }: {
    id: string;
    skuNumber: string;
    configurationName: string;
    category: string;
    skuDataConfigurations: string;
    nodeCount: number;
    defaultIp?: string | null;
  }) => {
    try {
      const catalogEntry = skuCatalog[skuNumber];

      if (!catalogEntry) {
        errorNotification(`Unknown SKU number: ${skuNumber}`);

        return;
      }

      const configData: { name: string; models: string[] }[] = JSON.parse(
        skuDataConfigurations
      );

      // Filter only AGX (nodes) — POE and LWOP are cameras and shown as device rows
      const merged: OnboardingComponent[] = catalogEntry.subComponents
        .filter(sub => sub.type !== "AGX")
        .map(sub => {
          const configEntry = configData.find(c => c.name === sub.name);

          return {
            name: sub.name,
            type: sub.type,
            makeModelId: sub.makeModelId,
            hasPT: sub.hasPT,
            models: configEntry?.models ?? [],
          };
        });

      setSelectedSKU({
        id,
        skuNumber,
        configurationName,
        category,
        skuDataConfigurations,
        nodeCount: newNodeCount,
      });

      setComponents(merged);

      const prefillIp = (defaultIp ?? "").replaceAll(" ", "");

      setDeviceIpAddresses(Array(merged.length).fill(prefillIp));

      setComponentSerialNumbers(Array(merged.length).fill(""));

      setSelectedNodes(
        Array(merged.length).fill({ nodeId: "", nodeIP: "", nodeName: "" })
      );

      // Initialize node rows for Step 1 (collision resolution happens in handleStep0Next)
      const initRows: NodeRow[] = Array.from(
        { length: newNodeCount },
        (_, i) => ({
          location: selectedLocation,
          nodeName: generateNodeName(selectedLocation?.title ?? "", i),
          nodeIP: "",
          componentSerialNumber: "",
        })
      );

      setNodeRows(initRows);

      setCreatedNodes([]);

      setNodesCreated(false);

      setStep0Submitted(false);

      setStep1Submitted(false);

      setStep2Submitted(false);

      refetchDevices(selectedLocation?.value ?? "");
    } catch {
      errorNotification("Invalid SKU Configuration data format");
    }
  };

  // Step 0: advance to node setup or devices
  const handleStep0Next = () => {
    setStep0Submitted(true);

    if (!selectedSKU) {
      errorNotification("Please select a SKU configuration");

      return;
    }

    if (!serialNumber) {
      errorNotification("Please enter a serial number");

      return;
    }

    if (devicePassword.length > 0 && devicePassword.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    // Resolve node name collisions before advancing to step 1
    // Resolve node name collisions before advancing to step 1
    if (nodeCount >= 1 && nodeRows.length > 0) {
      const existingNodeNames = new Set(
        allNodesData?.getNodes?.items
          ?.map(n => n?.nodeName ?? "")
          .filter(Boolean) ?? []
      );

      const rawNames = nodeRows.map(row => row.nodeName);
      const resolvedNames = resolveNodeNameConflicts(
        rawNames,
        existingNodeNames
      );

      const needsUpdate = rawNames.some((name, i) => name !== resolvedNames[i]);

      if (needsUpdate) {
        setNodeRows(prev =>
          prev.map((row, i) => ({ ...row, nodeName: resolvedNames[i] }))
        );
      }
    }

    setActiveStep(nodeCount >= 1 || isLinkAccessory ? 1 : deviceStepIndex);
  };

  // Step 1: update a single node row field
  const updateNodeRow = (
    index: number,
    field: keyof NodeRow,
    value: NodeRow[keyof NodeRow]
  ) => {
    setNodeRows(prev => {
      const updated = [...prev];

      updated[index] = { ...updated[index], [field]: value };

      return updated;
    });
  };

  // Step 1: create all nodes sequentially
  const handleCreateNodes = async () => {
    setStep1Submitted(true);

    const missing = nodeRows.some(r => !r.nodeIP.trim() || !r.location?.value);

    if (missing) {
      errorNotification("All nodes require a location and IP");

      return;
    }

    const invalidNodeIps = nodeRows.some(r => {
      if (!r.nodeIP.trim()) return false;

      return !IPV4_REGEX.test(r.nodeIP.trim());
    });

    if (invalidNodeIps) {
      errorNotification("One or more node IP addresses have invalid format");

      return;
    }

    setNodeCreationLoading(true);
    const created: CreatedNodeInfo[] = [];

    try {
      // Refetch to get the latest node data before creating
      await refetchAllNodes();

      for (let i = 0; i < nodeRows.length; i++) {
        const row = nodeRows[i];
        const input: AddNodeInput = {
          customerExternalId,
          locationId: row.location!.value,
          nodeName: row.nodeName,
          nodeIP: row.nodeIP,
          level: NodeLevel.NODE,
          tags: [],
        };

        try {
          const result = await createNode(input);

          if (result?.data?.addNode) {
            const node = result.data.addNode;
            const rawNodeId = node.nodeId ?? "";

            created.push({
              nodeId: rawNodeId
                .replace("C_", "C#")
                .replace("_L_", "#L#")
                .replace("_N_", "#N#"),
              nodeName: node.nodeName ?? "",
              nodeIP: node.nodeIP ?? "",
              onboardCommand: node.onboardCommand ?? "",
            });
          }
        } catch (nodeError: unknown) {
          const gqlErrors =
            nodeError instanceof Error && "graphQLErrors" in nodeError
              ? (nodeError as { graphQLErrors: { errorType?: string }[] })
                  .graphQLErrors
              : [];
          const isDuplicate = gqlErrors.some(
            e => e?.errorType === "NODE_ALREADY_EXISTS"
          );

          if (isDuplicate) {
            errorNotification(
              `Node '${row.nodeName}' already exists at this location. Please rename and try again.`
            );
            break;
          }

          throw nodeError;
        }
      }

      if (created.length === nodeRows.length) {
        setCreatedNodes(created);

        setNodesCreated(true);
      }
    } catch {
      errorNotification("Failed to create nodes");
    } finally {
      setNodeCreationLoading(false);
    }
  };

  // Step 1: accessory — select parent system and node then advance
  const handleStep1AccessoryNext = () => {
    setStep1AccessorySubmitted(true);

    if (!useStandaloneAccessory && !selectedParentProduct) return;

    if (!selectedAccessoryNode.nodeId) return;

    setSelectedNodes(Array(components.length).fill(selectedAccessoryNode));

    setActiveStep(deviceStepIndex);
  };

  // Step 1: continue to devices (update nodes with sku/serial, advance step)
  const handleContinueToDevices = async () => {
    for (let i = 0; i < createdNodes.length; i++) {
      const node = createdNodes[i];

      await updateNode({
        nodeId: node.nodeId
          .replaceAll("C_", "C#")
          .replaceAll("_L_", "#L#")
          .replaceAll("_N_", "#N#"),
        skuNumber: selectedSKU?.skuNumber ?? undefined,
        serialNumber: serialNumber || undefined,
        componentSerialNumber: nodeRows[i]?.componentSerialNumber || undefined,
        onboardedAt: Date.now(),
      });
    }

    // Pre-fill node assignment: all devices get the first created node by default
    const defaultNode = createdNodes[0]
      ? {
          nodeId: createdNodes[0].nodeId,
          nodeIP: createdNodes[0].nodeIP,
          nodeName: createdNodes[0].nodeName,
        }
      : { nodeId: "", nodeIP: "", nodeName: "" };

    setSelectedNodes(Array(components.length).fill(defaultNode));

    setActiveStep(deviceStepIndex);
  };

  // Step 2: node selection pool — if nodes were created in Step 1, limit to those
  const availableNodes: NodeAssignment[] = useMemo(() => {
    if (nodesCreated && createdNodes.length > 0) {
      return createdNodes.map((node, i) => ({
        nodeId: node.nodeId,
        nodeIP: node.nodeIP,
        nodeName: node.nodeName,
        location: nodeRows[i]?.location?.title ?? "",
      }));
    }

    return allCustomerNodes;
  }, [nodesCreated, createdNodes, allCustomerNodes, nodeRows]);

  const eligibleParentProducts = useMemo(
    () =>
      locationProducts.filter(
        p =>
          (p.skuNumber?.startsWith("001") || p.skuNumber?.startsWith("002")) &&
          !!p.serialNumber
      ),
    [locationProducts]
  );

  const accessoryEligibleNodes = useMemo(() => {
    if (useStandaloneAccessory) return allCustomerNodes;

    if (!selectedParentProduct?.nodeIds?.length) return [];

    const parentNodeIds = new Set(
      selectedParentProduct.nodeIds.filter(Boolean) as string[]
    );

    return allCustomerNodes.filter(n => parentNodeIds.has(n.nodeId));
  }, [useStandaloneAccessory, selectedParentProduct, allCustomerNodes]);

  const handleDeviceIpChange = (index: number, value: string) => {
    const cleaned = value.replaceAll(" ", "");

    setDeviceIpAddresses(prev => {
      const updated = [...prev];

      updated[index] = cleaned;

      return updated;
    });
  };

  const handleNodeSelect = (index: number, node: NodeAssignment) => {
    setSelectedNodes(prev => {
      const updated = [...prev];

      updated[index] = node;

      return updated;
    });
  };

  const handleComponentSerialChange = (index: number, value: string) => {
    setComponentSerialNumbers(prev => {
      const updated = [...prev];

      updated[index] = value;

      return updated;
    });
  };

  // Step 2: submit devices + services + product unit
  const saveDevices = async (): Promise<void> => {
    setStep2Submitted(true);

    if (!selectedSKU || components.length === 0) {
      errorNotification("Please select a SKU configuration");

      return;
    }

    const missingIps = deviceIpAddresses.some(ip => !ip.trim());

    if (missingIps) {
      errorNotification("All device IP addresses are required");

      return;
    }

    const invalidIps = deviceIpAddresses.some(ip => {
      if (!ip.trim()) return false;

      return !IPV4_REGEX.test(ip.trim());
    });

    if (invalidIps) {
      errorNotification("One or more device IP addresses have invalid format");

      return;
    }

    const missingNodes = selectedNodes.some(n => !n.nodeId);

    if (missingNodes) {
      errorNotification("All devices must be assigned to a node");

      return;
    }

    // Build template data — for hasPT components, auto-derive PT IP = device IP (CH4-UCv2)
    const templateData = components.map((comp, index) => ({
      name: comp.name,
      makeModelId: comp.makeModelId,
      deviceIp: deviceIpAddresses[index] || "",
      models: comp.models,
      isPT: comp.hasPT,
      panTiltIP: comp.hasPT ? deviceIpAddresses[index] : undefined,
      panTiltType: comp.hasPT ? "CH4-UCv2" : undefined,
      hasRecordVideo: true,
    }));

    const validation = validateCreateDevicesFromTemplate({
      parsedTemplateData: templateData,
      selectedNodes,
      devicePassword,
    });

    if (!validation.isValid) {
      errorNotification(validation.errorMessage!);

      return;
    }

    try {
      const devicesToCreate = mapTemplateDataToDeviceInput({
        templateData,
        selectedCustomerId,
        selectedLocation: selectedLocation ?? { title: "", value: "" },
        selectedNodes,
        devicePassword,
        skuNumber: selectedSKU.skuNumber,
        configurationName: selectedSKU.configurationName,
        serialNumber: serialNumber || undefined,
        componentSerialNumbers,
      });

      const uniqueDevices = checkDuplicateDeviceNames(
        existingNames,
        devicesToCreate
      );

      const servicesToCreate = mapTemplateDataToServiceInput(
        uniqueDevices,
        templateData,
        serialNumber || undefined
      );

      await batchCreateDevices(uniqueDevices);

      const serviceResponse = await batchCreateServices(servicesToCreate);

      if (serviceResponse?.errors) {
        errorNotification("Failed to create services");

        return;
      }

      // Record product unit
      const uniqueNodeIds = [
        ...Array.from(
          new Set(selectedNodes.map(n => n.nodeId).filter(Boolean))
        ),
      ];
      try {
        await addProduct({
          skuNumber: selectedSKU.skuNumber,
          serialNumber: serialNumber || "",
          configurationName: selectedSKU.configurationName,
          locationId: selectedLocation?.value ?? "",
          customerId: selectedCustomerId,
          nodeIds: uniqueNodeIds,
          parentSerialNumber: selectedParentProduct?.serialNumber ?? undefined,
        });
      } catch {
        console.error("Failed to record product");
      }

      closeDialog();

      successNotification("Product onboarded successfully!");

      try {
        await Promise.all(
          uniqueNodeIds.map(nodeId => syncShadowsForNode(nodeId))
        );
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("Device ID already exists");
      } else {
        errorNotification((error as ApolloError).message);
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      title="Onboard a Product"
      onClose={confirmDialogClose}
      showSubmitButton={false}
      maxWidth="xl"
      sx={{
        "& .MuiDialog-paper": { width: "95vw", maxWidth: "95vw" },
        overflow: "visible",
      }}
    >
      {/* Stepper header */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── Step 0: Configuration ── */}
      {activeStep === 0 && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Location
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedLocation?.title ?? "—"}
            </Typography>
          </Grid>

          <Grid item>
            <SKUSelect
              skuId={selectedSKU?.skuNumber ?? ""}
              onSKUChange={handleSKUChange}
              error={step0Submitted && !selectedSKU?.skuNumber}
              helperText={
                step0Submitted && !selectedSKU?.skuNumber
                  ? "SKU selection is required"
                  : ""
              }
            />
          </Grid>

          <Grid item>
            <TextField
              fullWidth
              label="Unit Serial Number (S/N)"
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              error={step0Submitted && !serialNumber}
              helperText={
                step0Submitted && !serialNumber
                  ? "Serial number is required"
                  : "Serial number for this physical unit"
              }
            />
          </Grid>

          <Grid item>
            <TextField
              fullWidth
              label="Device Password"
              value={devicePassword}
              onChange={e => setDevicePassword(e.target.value)}
              placeholder="Camera credentials (optional)"
              error={devicePassword.length > 0 && devicePassword.length < 8}
              helperText={
                devicePassword.length > 0 && devicePassword.length < 8
                  ? "Password must be at least 8 characters"
                  : "This will be the default credential used when onboarding devices. Can be overridden per onboarding."
              }
            />
          </Grid>
        </Grid>
      )}

      {/* ── Step 1: Node Setup ── */}
      {activeStep === 1 && nodeCount >= 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {nodeRows.map((row, index) => (
            <Box
              key={index}
              sx={{
                pb: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Node {index + 1}
              </Typography>

              <Box sx={{ display: "flex", width: "100%" }}>
                <Box sx={{ width: "33%", pr: 0.5 }}>
                  <LocationIdSelect
                    location={row.location}
                    defaultLocation={selectedLocation}
                    setLocation={loc => {
                      setNodeRows(prev => {
                        const updated = [...prev];
                        const currentRow = updated[index];

                        // Only regenerate node name if location actually changed
                        const locationChanged =
                          currentRow.location?.value !== loc?.value;

                        if (locationChanged) {
                          const existingNodeNames = new Set(
                            allNodesData?.getNodes?.items
                              ?.map(n => n?.nodeName ?? "")
                              .filter(Boolean) ?? []
                          );
                          const rawName = generateNodeName(
                            loc?.title ?? "",
                            index
                          );
                          const resolved = resolveNodeNameConflicts(
                            [rawName],
                            existingNodeNames
                          );

                          updated[index] = {
                            ...currentRow,
                            location: loc,
                            nodeName: resolved[0],
                          };
                        } else {
                          updated[index] = {
                            ...currentRow,
                            location: loc,
                          };
                        }

                        return updated;
                      });
                    }}
                    size="small"
                    hasError={step1Submitted && !row.location?.value}
                    helperText={
                      step1Submitted && !row.location?.value
                        ? "Location is required"
                        : ""
                    }
                  />
                </Box>

                <Box sx={{ width: "34%", px: 0.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Node IP"
                    value={row.nodeIP}
                    onChange={e =>
                      updateNodeRow(
                        index,
                        "nodeIP",
                        e.target.value.replaceAll(" ", "")
                      )
                    }
                    placeholder="192.168.1.x"
                    error={
                      step1Submitted &&
                      (!row.nodeIP.trim() ||
                        !IPV4_REGEX.test(row.nodeIP.trim()))
                    }
                    helperText={
                      step1Submitted && !row.nodeIP.trim()
                        ? "Node IP is required"
                        : step1Submitted && !IPV4_REGEX.test(row.nodeIP.trim())
                        ? "Invalid IP address format"
                        : ""
                    }
                  />
                </Box>

                <Box sx={{ width: "33%", pl: 0.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Node Component S/N"
                    value={row.componentSerialNumber}
                    onChange={e =>
                      updateNodeRow(
                        index,
                        "componentSerialNumber",
                        e.target.value
                      )
                    }
                    placeholder="Optional"
                  />
                </Box>
              </Box>

              {/* Onboard command — shown after node is created */}
              {nodesCreated && createdNodes[index]?.onboardCommand && (
                <Box sx={{ mt: 2, "& > div": { width: "100% !important" } }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block" }}
                  >
                    Run this script on {createdNodes[index].nodeName}:
                  </Typography>
                  <CodeSnippetBox
                    nodeScript={createdNodes[index].onboardCommand}
                    notify={() =>
                      successNotification("Script copied to clipboard!")
                    }
                  />
                </Box>
              )}
            </Box>
          ))}

          {/* Warning banner shown after nodes are created */}
          {nodesCreated && (
            <Alert severity="warning" sx={{ width: "fit-content" }}>
              Run the onboarding script on each node before continuing.
            </Alert>
          )}
        </Box>
      )}

      {/* ── Step 1: Node Selection (Link accessories only) ── */}
      {activeStep === 1 && isLinkAccessory && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Alert severity="info" sx={{ mb: 1 }}>
              This is a Link accessory. Choose whether to attach it to an
              existing system or deploy it standalone.
            </Alert>
          </Grid>

          <Grid item>
            <RadioGroup
              row
              value={useStandaloneAccessory ? "standalone" : "attached"}
              onChange={e => {
                const standalone = e.target.value === "standalone";

                setUseStandaloneAccessory(standalone);

                setSelectedParentProduct(null);

                setSelectedAccessoryNode({
                  nodeId: "",
                  nodeIP: "",
                  nodeName: "",
                });
              }}
            >
              <FormControlLabel
                value="attached"
                control={<Radio />}
                label="Attach to existing system"
              />
              <FormControlLabel
                value="standalone"
                control={<Radio />}
                label="Standalone — select any node"
              />
            </RadioGroup>
          </Grid>

          {!useStandaloneAccessory && (
            <Grid item>
              {eligibleParentProducts.length === 0 ? (
                <Alert severity="warning">
                  No compatible parent systems found at this location. Onboard a
                  Minerva or Halo system first.
                </Alert>
              ) : (
                <Autocomplete
                  fullWidth
                  options={eligibleParentProducts}
                  getOptionLabel={p =>
                    `${p.configurationName ?? p.skuNumber} — ${p.serialNumber}`
                  }
                  value={selectedParentProduct}
                  onChange={(_, val) => {
                    setSelectedParentProduct(val);

                    setSelectedAccessoryNode({
                      nodeId: "",
                      nodeIP: "",
                      nodeName: "",
                    });
                  }}
                  isOptionEqualToValue={(opt, val) =>
                    opt.serialNumber === val.serialNumber
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Parent System"
                      error={step1AccessorySubmitted && !selectedParentProduct}
                      helperText={
                        step1AccessorySubmitted && !selectedParentProduct
                          ? "Parent system is required"
                          : "Select the Minerva or Halo this Link is attached to"
                      }
                    />
                  )}
                />
              )}
            </Grid>
          )}

          <Grid item>
            <DeviceTemplateNodeSelect
              selectedNodeId={selectedAccessoryNode.nodeId}
              filteredNodes={accessoryEligibleNodes}
              onChange={node => setSelectedAccessoryNode(node)}
              error={step1AccessorySubmitted && !selectedAccessoryNode.nodeId}
              helperText={
                step1AccessorySubmitted && !selectedAccessoryNode.nodeId
                  ? "Node is required"
                  : useStandaloneAccessory
                  ? "Select the processing node for this Link"
                  : !selectedParentProduct
                  ? "Select a parent system first"
                  : ""
              }
              disabled={!useStandaloneAccessory && !selectedParentProduct}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      )}

      {/* ── Step 2: Devices & Submit ── */}
      {activeStep === deviceStepIndex && (
        <Grid container direction="column" spacing={2}>
          {components.length === 0 && (
            <Grid item>
              <Typography color="text.secondary">
                No device components found for this SKU.
              </Typography>
            </Grid>
          )}

          {components.map((comp, index) => (
            <Grid item key={comp.name + index}>
              <Box
                sx={{
                  mb: 1,
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Component header */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {comp.name}
                  <Chip
                    label={comp.type}
                    size="small"
                    sx={{ ml: 1, fontWeight: 600 }}
                  />
                  {comp.models.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {comp.models
                        .map(m => (m === "gasleak" ? "leak" : m))
                        .join(", ")}
                    </Typography>
                  )}
                </Typography>
                <Divider
                  sx={{
                    mb: 1.5,
                    mt: 0.5,
                    borderBottomWidth: 2,
                    borderColor: "primary.main",
                  }}
                />

                <Grid container spacing={1}>
                  {/* Device IP */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Device IP address"
                      value={deviceIpAddresses[index] || ""}
                      onChange={e =>
                        handleDeviceIpChange(index, e.target.value)
                      }
                      placeholder="192.168.1.x"
                      error={
                        step2Submitted &&
                        (!deviceIpAddresses[index]?.trim() ||
                          !IPV4_REGEX.test(deviceIpAddresses[index]?.trim()))
                      }
                      helperText={
                        step2Submitted && !deviceIpAddresses[index]?.trim()
                          ? "IP address is required"
                          : step2Submitted &&
                            !IPV4_REGEX.test(deviceIpAddresses[index]?.trim())
                          ? "Invalid IP address format"
                          : comp.hasPT
                          ? "PT IP auto-set to device IP (CH4-UCv2)"
                          : ""
                      }
                    />
                  </Grid>

                  {/* Component S/N */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Component S/N"
                      value={componentSerialNumbers[index] || ""}
                      onChange={e =>
                        handleComponentSerialChange(index, e.target.value)
                      }
                      placeholder="Optional"
                    />
                  </Grid>

                  {/* Node assignment */}
                  <Grid item xs={12}>
                    <DeviceTemplateNodeSelect
                      selectedNodeId={selectedNodes[index]?.nodeId ?? ""}
                      filteredNodes={availableNodes}
                      onChange={node => handleNodeSelect(index, node)}
                      error={step2Submitted && !selectedNodes[index]?.nodeId}
                      helperText={
                        step2Submitted && !selectedNodes[index]?.nodeId
                          ? "Node is required"
                          : ""
                      }
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Navigation buttons ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mt: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Back button */}
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={() => {
              if (
                activeStep === deviceStepIndex &&
                (nodeCount >= 1 || isLinkAccessory)
              ) {
                setActiveStep(1);
              } else {
                setActiveStep(0);
              }
            }}
          >
            Back
          </Button>
        )}

        {/* Step 0: Next */}
        {activeStep === 0 && (
          <Button variant="contained" onClick={handleStep0Next}>
            Next
          </Button>
        )}

        {/* Step 1: Accessory Next */}
        {activeStep === 1 && isLinkAccessory && (
          <Button variant="contained" onClick={handleStep1AccessoryNext}>
            Next
          </Button>
        )}

        {/* Step 1: Create Nodes or Continue */}
        {activeStep === 1 && nodeCount >= 1 && !nodesCreated && (
          <LoadingButton
            variant="contained"
            loading={nodeCreationLoading}
            onClick={handleCreateNodes}
          >
            Create Nodes
          </LoadingButton>
        )}
        {activeStep === 1 && nodeCount >= 1 && nodesCreated && (
          <Button variant="contained" onClick={handleContinueToDevices}>
            Continue to Device Setup
          </Button>
        )}

        {/* Step 2: Create Devices */}
        {activeStep === deviceStepIndex && (
          <LoadingButton
            variant="contained"
            loading={devicesLoading}
            onClick={saveDevices}
          >
            Create Devices
          </LoadingButton>
        )}
      </Box>
    </StyledDialog>
  );
};

export default CreateDevicesFromSKUDialog;
